import { useState } from "react";
import { X } from "lucide-react";

/**
 * Shared decline modal — used by both the BYOD protection step and the
 * landing-page Add-to-Deal CTA. Renders the product's customized heading +
 * text. If the product has a `secondChancePrice`, shows it as a one-time
 * discounted offer (Paint & Fabric flow). Otherwise just shows Keep Declined
 * / Add It / Learn More.
 */
export default function ProtectionDeclineModal({ product, onClose, onKeepDeclined, onAdd, onLearnMore }) {
  const [pending, setPending] = useState(false);
  if (!product) return null;

  const handleAdd = (acceptedSecondChance) => {
    setPending(true);
    onAdd?.({
      slug: product.slug,
      acceptedSecondChance,
      finalPrice: acceptedSecondChance ? product.secondChancePrice : (product.price || null),
    });
    setPending(false);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[var(--ssc-navy)]/80 flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog" aria-modal="true"
      data-testid={`decline-modal-${product.slug}`}
    >
      <div className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-5 sm:p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-1">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
              Second-chance offer
            </div>
            <h3 className="text-lg sm:text-xl font-black text-[var(--ssc-navy)] mt-1">
              {product.declineHeading}
            </h3>
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="p-2 -mr-2 text-[var(--ssc-ink-2)] hover:text-[var(--ssc-navy)]"
                  data-testid={`decline-modal-close-${product.slug}`}>
            <X size={20} />
          </button>
        </div>

        <p className="text-sm text-[var(--ssc-ink)] leading-relaxed">
          {product.declineText}
        </p>

        {/* Paint & Fabric second-chance pricing UI */}
        {product.secondChancePrice && (
          <div className="mt-4 bg-[var(--ssc-paper)] rounded-2xl p-4 border border-[var(--ssc-line)]">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-ink-2)]">Original Option</div>
                <div className="font-black text-[var(--ssc-ink)] line-through">${product.price.toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Today's Offer</div>
                <div className="text-2xl font-black text-emerald-700">${product.secondChancePrice.toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-[var(--ssc-ink-2)] mt-4 leading-relaxed">
          Coverage, benefits, limits, and exclusions may vary. See the actual contract for complete details.
        </p>

        <div className="mt-5 flex flex-col gap-2">
          {product.secondChancePrice ? (
            <button
              onClick={() => handleAdd(true)}
              disabled={pending}
              className="ssc-btn ssc-btn-primary w-full"
              data-testid={`decline-modal-add-second-chance-${product.slug}`}
            >
              Add {product.name} for ${product.secondChancePrice.toLocaleString()}
            </button>
          ) : (
            <button
              onClick={() => handleAdd(false)}
              disabled={pending}
              className="ssc-btn ssc-btn-primary w-full"
              data-testid={`decline-modal-add-${product.slug}`}
            >
              Add {product.name} to My Deal
            </button>
          )}
          <button
            onClick={() => { onKeepDeclined?.(product.slug); onClose?.(); }}
            className="ssc-btn ssc-btn-outline w-full"
            data-testid={`decline-modal-keep-declined-${product.slug}`}
          >
            Keep Declined
          </button>
          <button
            onClick={() => onLearnMore?.(product.slug)}
            className="text-sm font-semibold text-[var(--ssc-blue)] hover:underline py-2"
            data-testid={`decline-modal-learn-more-${product.slug}`}
          >
            Learn More
          </button>
        </div>
      </div>
    </div>
  );
}
